import {
  Box,
  Button,
  Card,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import {
  ScreenWrap,
  SearchInput,
  SelectInput,
  SoloLoading,
  CheckboxInput,
  ScreenError,
} from '../../components';
import { Plus, Star } from '@phosphor-icons/react';
import LibraryRow from './LibraryRow';
import { Link } from 'react-router';
import useModifiableParams from '../../hooks/use-modifiable-params';
import { ArrowForward } from '@mui/icons-material';
import { Api } from '../../api';
import { Model } from '../../models';

export default function LibraryListScreen() {
  const {
    getParams,
    setModifiedParams,
    tablePaginationProps,
    tableSortLabelProps,
  } = useModifiableParams();

  const params = getParams();

  const api = Api.LibraryContent.useReadCollectionAdminList({
    ...params,
    ['pagination' as any]: true,
  });

  const tagsApi = Api.Tag.useReadCollectionAll();

  if (api.error) return <ScreenError {...api} />;
  if (tagsApi.error) return <ScreenError {...tagsApi} />;

  const articles = api.data?.member;

  return (
    <ScreenWrap>
      <Stack spacing={3} alignItems="stretch">
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="h4" component="h1">
            Library Content
          </Typography>
          <Button
            variant="text"
            endIcon={<ArrowForward />}
            component={Link}
            to="/library/content-tags"
          >
            Show & Edit Tags
          </Button>
        </Stack>
        <Stack
          direction="row"
          alignItems="end"
          spacing={2}
          sx={{ width: '100%' }}
        >
          <Box minWidth={568}>
            <SearchInput
              label="Search"
              value={params.title || ''}
              onChange={(e) =>
                setModifiedParams({ title: e.target.value, page: '1' })
              }
              placeholder="Search by title"
            />
          </Box>
          <SelectInput
            label="Publish State"
            options={Model.LibraryContent.PUBLISH_STATE_OPTIONS}
            labelId="publish-state"
            value={params['exists[publishedAt]'] || ''}
            onChange={(value) =>
              setModifiedParams({ 'exists[publishedAt]': value, page: '1' })
            }
          />
          <SelectInput
            label="Type"
            labelId="type"
            value={params.type || ''}
            onChange={(value) => setModifiedParams({ type: value, page: '1' })}
            options={Model.LibraryContent.TYPE_OPTIONS}
          />
          <SelectInput
            label="Content Tag"
            labelId="content-tag"
            value={params.internalTags || ''}
            onChange={(value) =>
              setModifiedParams({ internalTags: value, page: '1' })
            }
            options={[
              { label: 'All', value: '' },
              ...(tagsApi.data?.member.map((t) => ({
                label: t.category.name + '/' + t.internalName,
                value: t['@id'],
              })) ?? []),
            ]}
          />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              height: 56,
              minWidth: 158,
            }}
          >
            <CheckboxInput
              label=""
              checkboxLabel="Only show featured content"
              labelId="featured-content"
              checked={params.isFeatured === 'true'}
              onChange={(checked) =>
                setModifiedParams({
                  isFeatured: checked ? 'true' : '',
                  page: '1',
                })
              }
            />
          </Box>
          <Box>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Plus />}
              component={Link}
              to="/library/new"
            >
              Create
            </Button>
          </Box>
        </Stack>

        <Stack spacing={2}>
          <Card>
            {articles ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Title</TableCell>
                      <TableCell>Publish State</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell sx={{ width: 250 }}>Content Tags</TableCell>
                      <TableCell>
                        <TableSortLabel {...tableSortLabelProps('lastUpdated')}>
                          Last updated
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="center"></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {articles.map((article) => (
                      <LibraryRow key={article.id} article={article} />
                    ))}
                  </TableBody>
                  <TableFooter
                    sx={{
                      borderTop: '1px solid rgba(224, 224, 224, 1)',
                    }}
                  >
                    <TableRow>
                      <TablePagination
                        {...tablePaginationProps({
                          totalItems: api.data?.totalItems ?? 0,
                          rowsPerPage: 10,
                        })}
                        count={api.data?.totalItems ?? 0}
                        colSpan={6}
                      />
                    </TableRow>
                  </TableFooter>
                </Table>
              </TableContainer>
            ) : (
              <SoloLoading />
            )}
          </Card>
          <Stack direction="row" gap={2}>
            <KeyItem color="warning" label="Draft" />
            <KeyItem color="success" label="Published" />
            <KeyItem color="success" label="Featured Content" featured />
          </Stack>
        </Stack>
      </Stack>
    </ScreenWrap>
  );
}

function KeyItem({
  color,
  label,
  featured,
}: {
  label: string;
  color: 'warning' | 'success';
  featured?: boolean;
}) {
  const colorMap = {
    warning: '#DE7101',
    success: 'success.main',
  };
  return (
    <Box display="flex" alignItems="center" gap={1}>
      {featured ? (
        <Star color="#FB9C0C" size={20} weight="fill" />
      ) : (
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            bgcolor: colorMap[color],
          }}
        />
      )}
      <Typography variant="body2">{label}</Typography>
    </Box>
  );
}
